import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';

interface OwnedSystem {
  id: string;
  name: string;
}

/** The owned systems before and after `currentId`, wrapping around; null when it is not owned. */
export function systemNeighbours(systems: readonly OwnedSystem[], currentId: string) {
  const index = systems.findIndex((s) => s.id === currentId);
  if (index < 0) return null;
  const at = (i: number) => systems[(i + systems.length) % systems.length] as OwnedSystem;
  return { index, prev: at(index - 1), next: at(index + 1) };
}

const arrow =
  'inline-flex size-11 items-center justify-center text-muted hover:text-text focus-visible:text-text';

/**
 * Previous / next owned system on the system screen, in the order of the Systems list (home first).
 * Keeps the open tab, so a player can upgrade the same building in every system in a row.
 */
export function SystemSwitcher({
  galaxyId,
  systems,
  currentId,
  search,
}: {
  galaxyId: string;
  systems: readonly OwnedSystem[];
  currentId: string;
  /** The current query string (`?tab=…`), carried over. */
  search: string;
}) {
  const near = systemNeighbours(systems, currentId);
  if (!near || systems.length < 2) return null;
  const to = (id: string) => `/g/${galaxyId}/systems/${id}${search}`;
  return (
    <nav aria-label="Your systems" data-system-switcher className="-my-3 flex items-center">
      <Link
        to={to(near.prev.id)}
        className={arrow}
        aria-label={`Previous system: ${near.prev.name}`}
      >
        <ChevronLeft className="size-5" aria-hidden />
      </Link>
      <span className="font-mono text-xs tabular-nums text-muted">
        {near.index + 1} / {systems.length}
      </span>
      <Link to={to(near.next.id)} className={arrow} aria-label={`Next system: ${near.next.name}`}>
        <ChevronRight className="size-5" aria-hidden />
      </Link>
    </nav>
  );
}
